import React, { useMemo } from "react";
import { useMediaQuery } from "@chakra-ui/react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import type { Report } from "../../../../types/reports";
import {
  groupReportsByTypeLane,
  REPORT_TYPE_LANE_ORDER,
  type ReportTypeLaneKey,
} from "../reportsTypeLanes";
import { ReportsTypeLane } from "./ReportsTypeLane";

export interface ReportsTypeBoardProps {
  reports: Report[];
  selectedIds: Set<string>;
  onSelectedChange: (reportId: string, selected: boolean) => void;
  onOpenReport: (report: Report) => void;
  onDeleteReport: (report: Report) => void;
  deletingReportId: string | null;
  isDeletingReports: boolean;
  onGenerateClick: (path: string) => void;
}

export function ReportsTypeBoard({
  reports,
  selectedIds,
  onSelectedChange,
  onOpenReport,
  onDeleteReport,
  deletingReportId,
  isDeletingReports,
  onGenerateClick,
}: ReportsTypeBoardProps): React.ReactElement {
  const [isDesktop] = useMediaQuery("(min-width: 1024px)");
  const grouped = useMemo(() => groupReportsByTypeLane(reports), [reports]);
  const [activeTab, setActiveTab] = React.useState<ReportTypeLaneKey>("trade-analysis");

  const laneProps = {
    selectedIds,
    onSelectedChange,
    onOpenReport,
    onDeleteReport,
    deletingReportId,
    isDeletingReports,
    onGenerateClick,
  };

  if (isDesktop) {
    return (
      <div
        data-reports-type-board=""
        className="grid w-full min-w-0 grid-cols-1 gap-4 lg:grid-cols-3"
      >
        {REPORT_TYPE_LANE_ORDER.map((lane) => (
          <ReportsTypeLane
            key={lane.key}
            lane={lane}
            reports={grouped[lane.key]}
            {...laneProps}
          />
        ))}
      </div>
    );
  }

  return (
    <Tabs
      data-reports-type-board-tabs=""
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as ReportTypeLaneKey)}
      className="w-full min-w-0"
    >
      <TabsList className="mb-3 h-auto w-full flex-wrap justify-start gap-1">
        {REPORT_TYPE_LANE_ORDER.map((lane) => (
          <TabsTrigger
            key={lane.key}
            value={lane.key}
            className="gap-1.5 px-2.5 py-1.5 font-[family-name:var(--font-primary)] text-xs normal-case tracking-normal"
          >
            <span className="truncate">{lane.label}</span>
            <Badge
              variant="outline"
              className="h-4 min-w-4 justify-center border-border px-1 font-[family-name:var(--font-primary)] text-[10px] font-normal"
            >
              {grouped[lane.key].length}
            </Badge>
          </TabsTrigger>
        ))}
      </TabsList>

      {REPORT_TYPE_LANE_ORDER.map((lane) => (
        <TabsContent key={lane.key} value={lane.key} className="mt-0 outline-none">
          <ReportsTypeLane
            lane={lane}
            reports={grouped[lane.key]}
            className="border-t-0"
            {...laneProps}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}
